// eslint-disable-next-line no-unused-vars
import {ErrorFlow, BrickContext, User} from 'olympe';
import {LAMBDA_NAMES} from './constants';

/**
 * To use this generic lambda, the lambda should have the following output: Control Flow as first output, and Error Flow as last output
 * @param {BrickContext} $
 * @param {*} lambda
 * @param {string[]} inputs
 * @param {function(string)} successCb
 * @param {function(any=)} failureCb
 */
const genericLambda = ($, lambda, inputs, successCb, failureCb) => {
    const lambdaInputs = lambda.getInputs();
    const lambdaOutputs = lambda.getOutputs();

    // We check if we provide a value for all the inputs (except the control flow)
    if (lambdaInputs.length - 1 !== inputs.length) {
        console.error('Inputs arrays are not corresponding.');
        failureCb();
        return;
    }

    const runner = $.runner(lambda);

    inputs.forEach((input, idx) => {
        runner.set(lambdaInputs[idx + 1], input); // We skip the first input (control flow) with idx + 1
    });
    runner.trigger(lambdaInputs[0]);

    Promise.any([
        runner.waitFor(lambdaOutputs[0]),
        runner.waitFor(lambdaOutputs[lambdaOutputs.length - 1]),
    ]).then((outputFlow) => {
        if (outputFlow instanceof ErrorFlow) {
            console.error(outputFlow);
            failureCb(outputFlow);
        } else {
            successCb(lambdaOutputs.slice(1, -1).map((output) => runner.get(output)));
        }
    })
        .catch((reason) => {
            console.error(reason);
            failureCb(reason);
        })
        .finally(() => runner.destroy());
};

/**
 * @param {BrickContext} $
 * @param {*} lambda
 * @param {[]} inputs
 * @return {Promise<string>}
 */
const genericLambdaPromise = ($, lambda, inputs) => {
    return new Promise((resolve, reject) => {
        try {
            const lambdaInputs = lambda.getInputs();
            const lambdaOutputs = lambda.getOutputs();

            // We check if we provide a value for all the inputs (except the control flow)
            if (lambdaInputs.length - 1 !== inputs.length) {
                reject(ErrorFlow.create('Inputs arrays are not corresponding.', 1));
                return;
            }

            const runner = $.runner(lambda);

            inputs.forEach((input, idx) => {
                runner.set(lambdaInputs[idx + 1], input); // We skip the first input (control flow) with idx + 1
            });
            runner.trigger(lambdaInputs[0]);

            Promise.race([
                runner.waitFor(lambdaOutputs[0]),
                runner.waitFor(lambdaOutputs[lambdaOutputs.length - 1]),
            ]).then((outputFlow) => {
                const outputs = lambdaOutputs.slice(1, -1).map((output) => runner.get(output));
                if (outputFlow instanceof ErrorFlow) {
                    console.error(outputFlow);
                    const err = new Error(outputFlow.getMessage());
                    err.outputs = outputs;
                    err.errorFlow = outputFlow;
                    reject(err);
                } else {
                    resolve(outputs);
                }
            }).catch((reason) => {
                console.error(reason);
                reject(reason);
            })
                .finally(() => runner.destroy());
        } catch (e) {
            reject(e);
        }
    });
};

/**
 * Normalize a rejection reason into an ErrorFlow.
 *
 * `genericLambdaPromise` rejects with a plain `Error` carrying the original ErrorFlow in `err.errorFlow`
 * (and the lambda outputs in `err.outputs`). It may also reject with a raw ErrorFlow (input mismatch) or with
 * whatever `runner.waitFor` threw. This helper hides those cases so callers can always rely on an ErrorFlow
 * (`getCode()`, `getMessage()`, ErrorFlow-typed outputs, serialization across contexts...).
 *
 * @param {*} reason the rejection reason (Error with `errorFlow`, ErrorFlow, Error, string, ...)
 * @param {number} [defaultCode=1] code used when the reason does not carry one
 * @return {ErrorFlow}
 */
export const toErrorFlow = (reason, defaultCode = 1) => {
    if (reason instanceof ErrorFlow) {
        return reason;
    }
    if (reason?.errorFlow instanceof ErrorFlow) {
        return reason.errorFlow;
    }
    const message = reason instanceof Error ? reason.message : String(reason);
    return ErrorFlow.create(message, defaultCode);
};

/**
 * @param {BrickContext} $
 * @param {*} lambda
 * @param {[]} inputs
 * @return {Promise<string>}
 */
const genericPSLLambdaPromise = ($, lambda, inputs) => {
    return new Promise((resolve, reject) => {
        try {
            const lambdaInputs = lambda.getInputs();
            const lambdaOutputs = lambda.getOutputs();

            // We check if we provide a value for all the inputs (except the control flow)
            if (lambdaInputs.length - 1 !== inputs.length) {
                reject(ErrorFlow.create('Inputs arrays are not corresponding.', 1));
                return;
            }

            const runner = $.runner(lambda);

            inputs.forEach((input, idx) => {
                runner.set(lambdaInputs[idx + 1], input); // We skip the first input (control flow) with idx + 1
            });
            runner.trigger(lambdaInputs[0]);

            Promise.race([
                runner.waitFor(lambdaOutputs[0]),
                runner.waitFor(lambdaOutputs[lambdaOutputs.length - 1]),
            ]).then((outputFlow) => {
                const result = lambdaOutputs.slice(1, -1).map((output) => runner.get(output));
                if (outputFlow instanceof ErrorFlow && !result?.[0].includes('No Valid MaterialId Found.') ) {
                    console.error(outputFlow);
                    reject(outputFlow);
                } else {
                    resolve(result);
                }
            }).catch((reason) => {
                console.error(reason);
                reject(reason);
            })
                .finally(() => runner.destroy());
        } catch (e) {
            reject(e);
        }
    });
};

/**
 *
 * @param {BrickContext} $
 * @param {*} lambda
 * @param {string} prToSubmit
 * @param {string} tag
 * @param {boolean} isItATestRun
 * @param {function(string)} successCb
 * @param {function(any=)} failureCb
 */
export const submitLambda = ($, lambda, prToSubmit, tag, isItATestRun, successCb, failureCb) => {
    genericLambda($, lambda, [prToSubmit, tag, isItATestRun], successCb, failureCb);
};

/**
 *
 * @param {BrickContext} $
 * @param {*} lambda
 * @param {string} prToPost
 * @param {string} prTag
 * @param {function()} successCb
 * @param {function()} failureCb
 */
export const postSubmittedRequestLambda = ($, lambda, prToPost, prTag, successCb, failureCb) => {
    genericLambda($, lambda, [prToPost, prTag], successCb, failureCb);
};

/**
 *
 * @param {BrickContext} $
 * @param {*} lambda
 * @param {string} prToSave
 * @param {string} prTag
 * @param {object[]} changeLog
 * @param {User} currentUser
 * @param {*} changeLogLambda
 * @param {function()} successCb
 * @param {function()} loadingCb
 * @param {function()} saveFailCb
 * @param {function()} changeLogFailureCb
 */
export const saveLambda = ($, lambda, prToSave, prTag, changeLog, currentUser, changeLogLambda, successCb, loadingCb, saveFailCb, changeLogFailureCb) => {
    const handleChangeLog = () => {
        if (!!changeLogLambda) {
            setToChangeLogLambda(
                $,
                changeLogLambda,
                changeLog,
                currentUser,
                prTag,
                () => {
                    successCb();
                    loadingCb();
                },
                (error) => {
                    changeLogFailureCb();
                    loadingCb();
                },
            );
        } else {
            successCb();
            loadingCb();
        }
    };
    genericLambda($, lambda, [prTag, prToSave], handleChangeLog, saveFailCb);
};

/**
 *
 * @param {BrickContext} $
 * @param {*} lambda
 * @param {string} data
 * @param {function()} cb
 * @param {function()} failureCb
 */
export const copyLambda = ($, lambda, data, cb, failureCb) => {
    const [
        controlFlowIn,
        dataIn,
    ] = lambda.getInputs();
    const [
        controlFlowOut,
        tagOut,
    ] = lambda.getOutputs();

    const copyRunner = $.runner(lambda)
        .set(dataIn, data)
        .trigger(controlFlowIn);

    copyRunner.waitFor(controlFlowOut).then((outputFlow) => {
        if (outputFlow instanceof ErrorFlow) {
            console.log(outputFlow);
            failureCb();
        } else {
            cb(copyRunner.get(tagOut));
        }
    }).finally(() => copyRunner.destroy());
};

/**
 *
 * @param {BrickContext} $
 * @param {*} lambda
 * @param {string} email
 * @param {string} requesterId
 * @param {function(string)} successCb
 * @param {function()} failureCb
 */
export const getUserIDAPI = ($, lambda, email, requesterId, successCb, failureCb) => {
    genericLambda($, lambda, [email, requesterId], successCb, failureCb);
};

/**
 *
 * @param {BrickContext} $
 * @param {*} lambda
 * @param {string} userID
 * @param {function(string)} successCb
 * @param {function()} failureCb
 */
export const getUserInfoByUserIDLambda = ($, lambda, userID, successCb, failureCb) => {
    genericLambda($, lambda, [userID], successCb, failureCb);
};

/**
 * @param {BrickContext} $
 * @param {*} lambda
 * @param {string} vendorCode
 * @param {LabelValue} purchaseOrg
 * @param {function(string)} successCb
 * @param {function()} failureCb
 */
export const getERSAndGRLambda = ($, lambda, vendorCode, purchaseOrg, successCb, failureCb) => {
    genericLambda($, lambda, [vendorCode, purchaseOrg.value], successCb, failureCb);
};

/**
 * @param {BrickContext} $
 * @param {*} lambda
 * @param {string} vendorCode
 * @param {LabelValue} purchaseOrg
 * @param {string} partnerFunction
 * @param {LabelValue} plant
 * @param {function(string)} successCb
 * @param {function()} failureCb
 */
export const invoicePartnerOrderingAddressLambda = async ($, lambda, vendorCode, purchaseOrg, partnerFunction, plant, successCb, failureCb) => {
    genericLambda($, lambda, [vendorCode, purchaseOrg.value, partnerFunction, plant.value], successCb, failureCb);
};

/**
 * @param {BrickContext} $
 * @param {string} lambda
 * @param {string} title
 * @param {string} text
 * @param {Color} color
 * @param {string} icon
 * @param {function()} cb
 */
export const showPopupLambda = ($, lambda, title, text, color, icon, cb) => {
    const [
        controlFlowIn,
        titleIn,
        textIn,
        colorIn,
        iconIn,
    ] = lambda.getInputs();
    const [
        Success,
    ] = lambda.getOutputs();

    const runner = $.runner(lambda)
        .set(titleIn, title)
        .set(textIn, text)
        .set(colorIn, color)
        .set(iconIn, icon)
        .trigger(controlFlowIn);

    runner.waitFor(Success).then(() => cb()).finally(() => runner.destroy());
};

/**
 * @param {BrickContext} $
 * @param {string} lambda
 * @param {string} tab
 * @param {string} searchText
 * @param {function()} cb
 */
export const changeTabLambda = ($, lambda, tab, searchText, cb) => {
    genericLambda($, lambda, [tab, searchText], cb, () => {});
};

/**
 * @param {BrickContext} $
 * @param {*} lambda
 * @param {string} tag
 * @param {number} take
 * @param {number} skip
 * @param {function(string[])} successCb
 * @param {function()} failureCb
 */
export const getChangeLogLambda = ($, lambda, tag, take, skip, successCb, failureCb) => {
    genericLambda($, lambda, [tag, take, skip], successCb, failureCb);
};

/**
 *
 * @param {BrickContext} $
 * @param {*} lambda
 * @param {object[]} logs
 * @param {User} currentUser
 * @param {string} tag
 * @param {function()} cb
 * @param {function(any=)} failureCb
 */
export const setToChangeLogLambda = ($, lambda, logs, currentUser, tag, cb, failureCb) => {
    genericLambda($, lambda, [logs, currentUser, tag], cb, failureCb);
};

/**
 *
 * @param {BrickContext} $
 * @param {*} lambda
 * @param {object[]} logs
 * @param {User} currentUser
 * @param {string} tag
 * @return {Promise<[]>}
 */
export const setToChangeLogPromiseLambda = ($, lambda, logs, currentUser, tag) => {
    return genericLambdaPromise($, lambda, [logs, currentUser, tag]);
};

/**
 * @param {BrickContext} $
 * @param {string} lambda
 * @param {string} materialNumber
 * @param {function(string)} successCb
 * @param {function()} failureCb
 */
export const getBaseUnitLambda = ($, lambda, materialNumber, successCb, failureCb) => {
    genericLambda($, lambda, [materialNumber], successCb, failureCb);
};

/**
 * @param {BrickContext} $
 * @param {string} lambda
 * @param {string[]} materialNumbers
 * @return {Promise<string>}
 */
export const getBaseUnitsLambda = ($, lambda, materialNumbers) => {
    return genericLambdaPromise($, lambda, [materialNumbers]);
};

/**
 * @param {BrickContext} $
 * @param {*} lambda
 * @param {string} companyCode
 * @param {string} requesterId
 * @param {string} purchaseOrg
 * @param {function(string)} successCb
 * @param {function()} failureCb
 */
export const getNewPaymentTermsLambda = ($, lambda, companyCode, requesterId, purchaseOrg, successCb, failureCb) => {
    genericLambda($, lambda, [companyCode, requesterId, purchaseOrg], successCb, failureCb);
};

/**
 * @param {BrickContext} $
 * @param {*} lambda
 * @param {string} paymentTerm
 * @param {string} companyCode
 * @param {string} purchaseOrg
 * @param {string} requesterId
 * @param {function(string)} successCb
 * @param {function()} failureCb
 */
export const getPaymentTermLambda = ($, lambda, paymentTerm, companyCode, purchaseOrg, requesterId, successCb, failureCb) => {
    genericLambda($, lambda, [paymentTerm, companyCode, purchaseOrg, requesterId], successCb, failureCb);
};


/**
 * @param {BrickContext} $
 * @param {*} lambda
 * @param {string} companyCode
 * @param {function(string)} successCb
 */
export const getCurrencyFromCompanyCodeLambda = ($, lambda, companyCode, successCb) => {
    genericLambda($, lambda, [companyCode], successCb, () => {});
};

/**
 * @param {BrickContext} $
 * @param {*} lambda
 * @param {string} group
 * @param {function(string)} cb
 */
export const getEmailByGroupingNameLambda = async ($, lambda, group, cb) => {
    genericLambda($, lambda, [group], cb, () => {});
};

/**
 * @param {BrickContext} $
 * @param {*} lambda
 * @param {function(string)} successCb
 * @param {function()} failureCb
 */
export const getUserDefaultsLambda = ($, lambda, successCb, failureCb) => {
    genericLambda($, lambda, [],
        (result) => {
            successCb(result[0].getLabelValueObject());
        }
        , failureCb);
};

/**
 * @param {BrickContext} $
 * @param {*} lambda
 * @param {*} currentUser
 * @param {function(string)} successCb
 * @param {function()} failureCb
 */
export const getUserDefaultsWithParameterLambda = ($, lambda, currentUser, successCb, failureCb) => {
    genericLambda($, lambda, [currentUser],
        (result) => {
            successCb(result[0].getLabelValueObject());
        }
        , failureCb);
};

/**
 * @param {BrickContext} $
 * @param {*} lambda
 * @return {Promise<[]>}
 */
export const getUserDefaultsPromiseLambda = ($, lambda) => {
    return genericLambdaPromise($, lambda, []);
};

/**
 * @param {BrickContext} $
 * @param {*} lambda
 * @param {string} userId
 * @param {function(string)} successCb
 * @param {function(any=)} failureCb
 */
export const getAuthorizationDataLambda = ($, lambda, userId, successCb, failureCb) => {
    genericLambda($, lambda, [userId, '', '', '', ''], successCb, failureCb);
};

/**
 * @param {BrickContext} $
 * @param {*} lambda
 * @param {string} userId
 * @param {string} purchaseOrg
 * @param {string} purchasingGroup
 * @param {string} plant
 * @param {string} companyCode
 * @param {function(string)} successCb
 * @param {function(string)} failureCb
 */
export const getAuthorizationFromDataLambda = ($, lambda, userId, purchaseOrg, purchasingGroup, plant, companyCode, successCb, failureCb) => {
    genericLambda($, lambda, [userId, purchaseOrg, purchasingGroup, plant, companyCode], successCb, failureCb);
};

/**
 * @param {BrickContext} $
 * @param {*} lambda
 * @param {string} userId
 * @param {string} purchaseOrg
 * @param {string} purchasingGroup
 * @param {string} plant
 * @param {string} companyCode
 * @return {Promise<[]>}
 */
export const getAuthorizationFromDataWithPromiseLambda = ($, lambda, userId, purchaseOrg, purchasingGroup, plant, companyCode) => {
    return genericLambdaPromise($, lambda, [userId, purchaseOrg, purchasingGroup, plant, companyCode]);
};

/**
 * @param {BrickContext} $
 * @param {*} lambda
 * @param {string} supplier
 * @param {string} purchaseOrg
 * @return {Promise<[]>}
 */
export const getIncotermsWithPromiseLambda = ($, lambda, supplier, purchaseOrg) => {
    return genericLambdaPromise($, lambda, [supplier, purchaseOrg]);
};

/**
 * @param {BrickContext} $
 * @param {*} lambda
 * @return {Promise<[]>}
 */
export const getAllIncotermsWithPromiseLambda = ($, lambda) => {
    return genericLambdaPromise($, lambda, []);
};

/**
 * @param {BrickContext} $
 * @param {*} lambda
 * @param {string} vendorCode
 * @param {string} purchaseOrg
 * @return {Promise}
 */
export const getERSAndGRWithPromiseLambda = ($, lambda, vendorCode, purchaseOrg) => {
    return genericLambdaPromise($, lambda, [vendorCode, purchaseOrg]);
};

/**
 * @param {BrickContext} $
 * @param {*} lambda
 * @param {string} vendorCode
 * @param {string} purchaseOrg
 * @param {string} partnerFunction
 * @param {string} plant
 * @return {Promise}
 */
export const getPartnerFunctionWithPromiseLambda = ($, lambda, vendorCode, purchaseOrg, partnerFunction, plant) => {
    return genericLambdaPromise($, lambda, [vendorCode, purchaseOrg, partnerFunction, plant]);
};


/**
 * @param {BrickContext} $
 * @param {*} lambda
 * @param {string} table
 * @param {object[]} data
 * @param {string} actionType
 * @return {Promise<string>}
 */
export const uploadRecordsLambda = ($, lambda, table, data, actionType) => {
    return genericLambdaPromise($, lambda, [table, data, actionType]);
};

/**
 * @param {BrickContext} $
 * @param {*} lambda
 * @param {string} columns
 * @param {string} table
 * @param {array} filterState
 * @param {string} orderByColumnName
 * @param {string} orderByValue
 * @param {number} count
 * @param {function(string)} successCb
 * @param {function(string)} failureCb
 */
export const downloadRecordsLambda = ($, lambda, columns, table, filterState, orderByColumnName, orderByValue, count, successCb, failureCb) => {
    genericLambda($, lambda, [columns, table, filterState, orderByColumnName, orderByValue, count], successCb, failureCb);
};

/**
 * @param {BrickContext} $
 * @param {*} lambda
 * @param {string} tag
 * @param {string} sheetName
 * @param {function(string)} successCb
 * @param {function(string)} failureCb
 */
export const getJsonFromExcelLambda = ($, lambda, tag, sheetName, successCb, failureCb) => {
    genericLambda($, lambda, [tag, sheetName], successCb, failureCb);
};

/**
 * @param {BrickContext} $
 * @param {*} lambda
 * @param {string} tag
 * @param {function(string)} successCb
 * @param {function(string)} failureCb
 */
export const getExcelToPRLambda = ($, lambda, tag, successCb, failureCb) => {
    genericLambda($, lambda, [tag], successCb, failureCb);
};

/**
 * @param {BrickContext} $
 * @param {*} lambda
 * @param {string} tableName
 * @param {{filterColumn: string, filterValue: string}[]} filterState
 * @param {number} limit
 * @param {number} offset
 * @return {Promise<string>}
 */
export const olympeDBReadLambda = ($, lambda, tableName, filterState, limit, offset) => {
    return genericLambdaPromise($, lambda, [tableName, filterState, limit, offset]);
};


/**
 * @param {BrickContext} $
 * @param {*} lambda
 * @param {string} tableName
 * @param {number} index
 * @return {Promise<string>}
 */
export const olympeDBDeleteLambda = ($, lambda, tableName, index) => {
    return genericLambdaPromise($, lambda, [tableName, index]);
};

/**
 * @param {BrickContext} $
 * @param {*} lambda
 * @param {string} tableName
 * @param {{}} values
 * @return {Promise<string>}
 */
export const olympeDBUpdateLambda = ($, lambda, tableName, values) => {
    return genericLambdaPromise($, lambda, [tableName, values]);
};

/**
 * @param {BrickContext} $
 * @param {*} lambda
 * @param {string} tableName
 * @param {{}} values
 * @return {Promise<string>}
 */
export const olympeDBCreateLambda = ($, lambda, tableName, values) => {
    return genericLambdaPromise($, lambda, [tableName, values]);
};

/**
 *
 * @param {BrickContext} $
 * @param {*} lambda
 * @param {string} companyCode
 * @param {boolean} isOmvOrCMV
 * @param {string} userId
 * @param {function()} successCb
 * @param {function()} failureCb
 */
export const getCBJustificationThresholdLambda = ($, lambda, companyCode, isOmvOrCMV, userId, successCb, failureCb) => {
    genericLambda($, lambda, [companyCode, isOmvOrCMV, userId], successCb, failureCb);
};

/**
 * @param {BrickContext} $
 * @param {*} lambda
 * @param {string} prJSON
 * @param {string} tag
 * @param {function()} successCb
 * @param {function()} failureCb
 */
export const getConsumptionLevelLambda = ($, lambda, prJSON, tag, successCb, failureCb ) => {
    genericLambda($, lambda, [prJSON, tag], successCb, failureCb);
};

/**
 *
 * @param {BrickContext} $
 * @param {*} lambda
 * @param {boolean} useAutomation
 * @param {string} wayOfBuying
 * @param {string} supplierNumber
 * @param {string} purchaseOrg
 * @param {string} companyCode
 * @param {string} startDate
 * @param {string} endDate
 * @param {string} materialNumber
 * @param {string} term
 * @param {string} requesterId
 * @param {string} l3Category
 * @param {function()} successCb
 * @param {function()} failureCb
 */
export const getTaxCodeLambda = ($, lambda, useAutomation, wayOfBuying, supplierNumber, purchaseOrg, companyCode, startDate, endDate, materialNumber, term, requesterId, l3Category, successCb, failureCb) => {
    genericLambda($, lambda, [useAutomation,
        wayOfBuying,
        supplierNumber,
        purchaseOrg,
        companyCode,
        startDate,
        endDate,
        materialNumber,
        term,
        requesterId,
        l3Category], successCb, failureCb);
};

/**
 * @param {BrickContext} $
 * @param {any} lambda
 * @param {string} eventCategory
 * @param {string} eventAction
 * @param {string} eventName
 * @param {object} eventValue
 * @param {function()} successCb
 */
export const matomoTrackEventLambda = ($, lambda, eventCategory, eventAction, eventName, eventValue, successCb) => {
    const [
        controlFlowIn,
        eventCategoryIn,
        eventActionIn,
        eventNameIn,
        eventValueIn,
    ] = lambda.getInputs();
    const [
        successOut,
    ] = lambda.getOutputs();

    const runner = $.runner(lambda)
        .set(eventCategoryIn, eventCategory)
        .set(eventActionIn, eventAction)
        .set(eventNameIn, eventName)
        .set(eventValueIn, eventValue)
        .trigger(controlFlowIn);

    runner.waitFor(successOut)
        .then(() => successCb)
        .catch((reason) => console.error(reason))
        .finally(() => runner.destroy());
};

/**
 * @param {BrickContext} $
 * @param {*} lambda
 * @param {string} vendor
 * @param {string} purchaseOrg
 * @param {string} requesterId
 * @param {function()} successCb
 * @param {function()} failureCb
 */
export const getDefaultPaymentTermsLambda = ($, lambda, vendor, purchaseOrg, requesterId, successCb, failureCb) => {
    genericLambda($, lambda, [vendor, purchaseOrg, requesterId], successCb, failureCb);
};

/**
 * @param {BrickContext} $
 * @param {*} lambda
 * @param {string} searchInput
 * @param {string} requesterId
 * @param {function()} successCb
 * @param {function()} failureCb
 */
export const getEmailsAPILambda = ($, lambda, searchInput, requesterId, successCb, failureCb) => {
    genericLambda($, lambda, [searchInput, requesterId], successCb, failureCb);
};

/**
 * @param {BrickContext} $
 * @param {*} lambda
 * @param {string} email
 * @return {Promise<string>}
 */
export const getUserEmailsGraphAPILambda = ($, lambda, email) => {
    return genericLambdaPromise($, lambda, [email]);
};


/**
 * @param {BrickContext} $
 * @param {*} lambda
 * @param {string} materialNr
 * @param {function()} successCb
 * @param {function()} failureCb
 */
export const getL3CategoryLambda = ($, lambda, materialNr, successCb, failureCb) => {
    genericLambda($, lambda, [materialNr], successCb, failureCb);
};

/**
 * @param {BrickContext} $
 * @param {*} lambda
 * @param {string} searchString
 * @param {string} userId
 * @param {function()} successCb
 * @param {function()} failureCb
 */
export const getUsersAPILambda = ($, lambda, searchString, userId, successCb, failureCb) => {
    genericLambda($, lambda, [searchString, userId], successCb, failureCb);
};

/**
 * @param {BrickContext} $
 * @param {*} lambda
 * @param {string} searchString
 * @param {string} userId
 * @return {Promise<string>} */
export const getUsersGraphAPILambda = ($, lambda, searchString) => {
    return genericLambdaPromise($, lambda, [searchString]);
};

/**
 * @param {BrickContext} $
 * @param {*} lambda
 * @param {string} supplier
 * @param {string} purchaseOrg
 * @param {function()} successCb
 * @param {function()} failureCb
 */
export const getIncotermsLambda = ($, lambda, supplier, purchaseOrg, successCb, failureCb) => {
    genericLambda($, lambda, [supplier, purchaseOrg], successCb, failureCb);
};

/**
 * @param {BrickContext} $
 * @param {*} lambda
 * @param {function()} successCb
 * @param {function()} failureCb
 */
export const getAllIncotermsLambda = ($, lambda, successCb, failureCb) => {
    genericLambda($, lambda, [], successCb, failureCb);
};

/**
 * @param {BrickContext} $
 * @param {*} lambda
 * @param {string} companyCode
 * @param {string} purchaseOrg
 * @param {string} purchasingGroup
 * @param {string} plant
 * @param {string} vendor
 * @param {string} userId
 * @param {function()} successCb
 * @param {function()} failureCb
 */
export const getP2POutputTableLambda = ($, lambda, companyCode, purchaseOrg, purchasingGroup, plant, vendor, userId, successCb, failureCb) => {
    genericLambda($, lambda, [companyCode, purchaseOrg, purchasingGroup, plant, vendor, userId], successCb, failureCb);
};

export const newSubmitPRLambda = ($, lambda, pr, tag, isCBSectionDisplayed, approvedBySB, approvedByOB, userIDForSAPAPIs, successCb, failureCb) => {
    genericLambda($, lambda, [pr, tag, isCBSectionDisplayed, approvedBySB, approvedByOB, userIDForSAPAPIs], successCb, failureCb);
};

/**
 * @param {BrickContext} $
 * @param {*} lambda
 * @param {string} companyCode
 * @param {string} purchaseOrg
 * @param {string} plant
 * @param {string} vendorCode
 * @param {string} costCenter
 * @param {string} glAccount
 * @return {Promise<string>}
 */
export const mlGetL3CategoryLambda = ($, lambda, companyCode, purchaseOrg, plant, vendorCode, costCenter, glAccount) => {
    return genericLambdaPromise($, lambda, [companyCode, purchaseOrg, plant, vendorCode, costCenter, glAccount]);
};

/**
 * @param {BrickContext} $
 * @param {*} lambda
 * @return {Promise<string>}
 */
export const getAllL3CategoriesLambda = ($, lambda) => {
    return genericLambdaPromise($, lambda, []);
};

/**
 * @param {BrickContext} $
 * @param {*} lambda
 * @param {string} plant
 * @param {string} l3Category
 * @param {string} code
 * @param {string} name
 * @return {Promise<string>}
 */
export const getItemCodeDescLambda = ($, lambda, plant, l3Category, code, name) => {
    return genericLambdaPromise($, lambda, [plant, l3Category, code, name]);
};

/**
 * @param {BrickContext} $
 * @param {*} lambda
 * @param {string} emails
 * @param {string} requesterId
 * @return {Promise<string>}
 */
export const getUserIdLambda = ($, lambda, emails, requesterId) => {
    return genericLambdaPromise($, lambda, [emails, requesterId]);
};

/**
 * @param {BrickContext} $
 * @param {*} lambda
 * @param {string[]} emails
 * @return {Promise<string>}
 */
export const getEmailByUserIdGraphAPILambda = ($, lambda, emails) => {
    return genericLambdaPromise($, lambda, [emails]);
};

/**
 * @param {BrickContext} $
 * @param {*} lambda
 * @param {string} to
 * @param {string} cc
 * @param {string} bcc
 * @param {string} subject
 * @param {string} content
 * @param {[]} attachment
 * @return {Promise<string>}
 */
export const sendEmailLambda = ($, lambda, to, cc, bcc, subject, content, attachment) => {
    return genericLambdaPromise($, lambda, [to, cc, bcc, subject, content, attachment]);
};

/**
 * @param {BrickContext} $
 * @param {*} lambda
 * @return {Promise<string>}
 */
export const downloadToExcelLambda = ($, lambda) => {
    return genericLambdaPromise($, lambda, []);
};

/**
 * @param {BrickContext} $
 * @param {*} lambda
 * @param {string} tag
 * @param {string} prJson
 * @return {Promise<string>}
 */
export const saveWithoutChangeLogLambda = ($, lambda, tag, prJson) => {
    return genericLambdaPromise($, lambda, [tag, prJson]);
};

/**
 * @param {BrickContext} $
 * @param {*} lambda
 * @param {string} materialId
 * @return {Promise<string>}
 */
export const getMaterialTypePromiseLambda = ($, lambda, materialId) => {
    return genericLambdaPromise($, lambda, [materialId]);
};

/**
 * @param {BrickContext} $
 * @param {*} lambda
 * @param {string} materialId
 * @param {string} plant
 * @return {Promise<string>}
 */
export const getMaterialByCodePromiseLambda = ($, lambda, materialId, plant) => {
    return genericLambdaPromise($, lambda, [materialId, plant]);
};

/**
 * @param {BrickContext} $
 * @param {*} lambda
 * @param {string} materialId
 * @return {Promise<string>}
 */
export const getL3CategoryFromMaterialPromiseLambda = ($, lambda, materialId) => {
    return genericLambdaPromise($, lambda, [materialId]);
};

/**
 * @param {BrickContext} $
 * @param {any} lambda
 * @param {string} tag
 * @return {Promise<string>}
 */
export const deletePRLambda = ($, lambda, tag) => {
    return genericLambdaPromise($, lambda, [tag]);
};

/**
 * @param {BrickContext} $
 * @param {any} lambda
 * @param {string} tag
 * @return {Promise<string>}
 */
export const getPRStatusFromBackendLambda = ($, lambda, tag) => {
    return genericLambdaPromise($, lambda, [tag]);
};

/**
 * @param {BrickContext} $
 * @param {any} lambda
 * @return {Promise<boolean[]>}
 */
export const getCompanyCodeDisablingDYNLambda = ($, lambda) => {
    return genericLambdaPromise($, lambda, []);
};

/**
 * @param {BrickContext} $
 * @param {any} lambda
 * @return {Promise<boolean[]>}
 */
export const isCandexEnabledLambda = ($, lambda) => {
    return genericLambdaPromise($, lambda, []);
};

/**
 * @param {BrickContext} $
 * @param {any} lambda
 * @return {Promise<boolean[]>}
 */
export const isNestBuyCCEnabledLambda = ($, lambda) => {
    return genericLambdaPromise($, lambda, []);
};

/**
 * @param {BrickContext} $
 * @param {any} lambda
 * @param {string} input
 * @return {Promise<string[]>}
 */
export const getCompanyCodesLambda = ($, lambda, input) => {
    return genericLambdaPromise($, lambda, [input]);
};

/**
 * @param {BrickContext} $
 * @param {any} lambda
 * @param {string} property
 * @param {string} searchInput
 * @param {string} companyCode
 * @param {string} plant
 * @param {string} wbs
 * @param {string} materialNumber
 * @param {string} itemCode
 * @param {string} l3Category
 * @param {string} userId
 * @return {Promise<string>}
 */
export const getMasterDataLambda = ($, lambda, property, searchInput, companyCode, plant, wbs, materialNumber, itemCode, l3Category, userId) => {
    return genericLambdaPromise($, lambda, [property, searchInput, companyCode, plant, wbs, materialNumber, itemCode, l3Category, userId]);
};

/**
 * @param {BrickContext} context
 * @param {string} groupingName
 * @return {Promise<string>}
 */
export const getEmailByGroupingNameSB = (context, groupingName) => {
    const getEmailByGroupingName = context.get(LAMBDA_NAMES.GET_EMAIL_BY_GROUPING_NAME);

    return new Promise((resolve, reject) => {
        const [
            controlFlowIn,
            groupingNameIn,
        ] = getEmailByGroupingName.getInputs();

        const [
            SuccessOut,
            resultOut,
            ErrorOut,
        ] = getEmailByGroupingName.getOutputs();

        const getEmailByGroupingNameRunner = context.runner(getEmailByGroupingName)
            .set(groupingNameIn, groupingName)
            .trigger(controlFlowIn);

        Promise.any([
            getEmailByGroupingNameRunner.waitFor(SuccessOut),
            getEmailByGroupingNameRunner.waitFor(ErrorOut),
        ]).then((outputFlow) => {
            if (outputFlow instanceof ErrorFlow) {
                reject(outputFlow);
            } else {
                resolve(getEmailByGroupingNameRunner.get(resultOut));
            }
        }).catch(reject)
            .finally(() => getEmailByGroupingNameRunner.destroy());
    });
};


/**
 * @param {BrickContext} $
 * @param {*} lambda
 * @param {string} purchaseOrg
 * @param {string} companyCode
 * @param {string} userId
 * @param {function()} successCb
 * @param {function()} failureCb
 */
export const getConditionTypesLambda = ($, lambda, purchaseOrg, companyCode, userId, successCb, failureCb) => {
    genericLambda($, lambda, [purchaseOrg, companyCode, userId], successCb, failureCb);
};

/**
 * @param {BrickContext} $
 * @param {*} lambda
 * @param {string} plantCode
 * @param {string} plantAddressNumber
 * @param {string} storageLocation
 * @param {string} userId
 * @return {Promise<[]>}
 */
export const getAddressFromPlantCodeLambda = ($, lambda, plantCode, plantAddressNumber, storageLocation, userId) => {
    return genericLambdaPromise($, lambda, [plantCode, plantAddressNumber, storageLocation, userId]);
};

/**
 * @param {BrickContext} $
 * @param {any} lambda
 * @param {string[]} prTags
 * @param {string} userID
 * @param {object} currentUserObject
 * @return {Promise<[]>}
 */
export const massSubmitLambda = ($, lambda, prTags, userID, currentUserObject) => {
    return genericLambdaPromise($, lambda, [prTags, userID, currentUserObject]);
};


/**
 * @param {BrickContext} $
 * @param {any} lambda
 * @param {PurchaseRequestDM} pr
 * @param {string} currentUser
 * @param {object} currentUserObject
 * @return {Promise<[]>}
 */
export const massSubmitPromiseLambda = ($, lambda, pr, currentUser, currentUserObject) => {
    return genericLambdaPromise($, lambda, [pr, currentUser, currentUserObject]);
};

/**
 * @param {BrickContext} $
 * @param {any} lambda
 * @param {string[]} prTags
 * @param {string} userID
 * @return {Promise<[]>}
 */
export const massDeleteLambda = ($, lambda, prTags, userID) => {
    return genericLambdaPromise($, lambda, [prTags, userID]);
};

/**
 * @param {BrickContext} $
 * @param {any} lambda
 * @param {string} pr
 * @param {string} currentUser
 * @return {Promise<[]>}
 */
export const massDeletePromiseLambda = ($, lambda, pr, currentUser) => {
    return genericLambdaPromise($, lambda, [pr, currentUser]);
};

/**
 * @param {BrickContext} $
 * @param {any} lambda
 * @param {string[]} prTags
 * @param {string} userID
 * @param {*} currentUserObject
 * @return {Promise<[]>}
 */
export const massCopyLambda = ($, lambda, prTags, userID, currentUserObject) => {
    return genericLambdaPromise($, lambda, [prTags, userID, currentUserObject]);
};

/**
 * @param {BrickContext} $
 * @param {any} lambda
 * @param {PurchaseRequestDM} pr
 * @param {string} currentUser
 * @param {*} currentUserObject
 * @param {string} currentTab
 * @return {Promise<[]>}
 */
export const massCopyPromiseLambda = ($, lambda, pr, currentUser, currentUserObject, currentTab) => {
    return genericLambdaPromise($, lambda, [pr, currentUser, currentUserObject, currentTab]);
};

/**
 * @param {BrickContext} $
 * @param {any} lambda
 * @param {string} prJson
 * @return {Promise<string>}
 */
export const createPrFromExcelLambda = ($, lambda, prJson) => {
    return genericLambdaPromise($, lambda, [prJson]);
};

/**
 * @param {BrickContext} $
 * @param {any} lambda
 * @param {string[]} materialIds
 * @return {Promise<[]>}
 */
export const getPSLStatusLambda = ($, lambda, materialIds) => {
    return genericLambdaPromise($, lambda, [materialIds]);
};

/**
 * @param {BrickContext} $
 * @param {any} lambda
 * @param {string[]} materialIds
 * @return {Promise<[]>}
 */
export const getPSLInformationLambda = ($, lambda, materialIds) => {
    return genericPSLLambdaPromise($, lambda, [materialIds]);
};

/**
 * @param {BrickContext} $
 * @param {any} lambda
 * @param {string} vendorCode
 * @param {string} purchaseOrg
 * @return {Promise<[]>}
 */
export const validateSupplierWithPurchaseOrgLambda = ($, lambda, vendorCode, purchaseOrg) => {
    return genericLambdaPromise($, lambda, [vendorCode, purchaseOrg]);
};

/**
 * @param {BrickContext} $
 * @param {any} lambda
 * @param {string} tag
 * @return {Promise<[]>}
 */
export const getEPRIdFromTagLambda = ($, lambda, tag) => {
    return genericLambdaPromise($, lambda, [tag]);
};

/**
 *
 * @param {BrickContext} $
 * @param {*} lambda
 * @param {string} searchText
 * @param {function()} cb
 * @param {function(any=)} failureCb
 */
export const setFilterToAllLambda = ($, lambda, searchText, cb, failureCb) => {
    genericLambda($, lambda, [searchText], cb, failureCb);
};

export const getMasterDataMultipleObjectsLambda = ($, lambda, propertyName, searchValue) => {
    return genericLambdaPromise($, lambda, [propertyName, searchValue]);
};

/**
 * @param {BrickContext} $
 * @param {any} lambda
 * @param {string} companyCodes
 * @param {string} purchaseOrgs
 * @param {string} purchasingGroups
 * @param {string} plants
 * @return {Promise<string>}
 */
export const getAuthObjectsFromSubmittedRequestsLambda = ($, lambda, companyCodes, purchaseOrgs, purchasingGroups, plants) => {
    return genericLambdaPromise($, lambda, [companyCodes, purchaseOrgs, purchasingGroups, plants]);
};

/**
 * @param {BrickContext} $
 * @param {any} lambda
 * @param {string} materialNumber
 * @return {Promise<string>}
 */
export const getMaterialTypeLambda = ($, lambda, materialNumber) => {
    return genericLambdaPromise($, lambda, [materialNumber]);
};

/**
 * @param {BrickContext} $
 * @param {*} lambda
 * @param {string} companyCode
 * @param {function()} successCb
 * @param {function()} failureCb
 */
export const checkCompanyCodeLambda = async ($, lambda, companyCode, successCb, failureCb) => {
    genericLambda($, lambda, [companyCode], successCb, failureCb);
};

/**
 * @param {BrickContext} $
 * @param {*} lambda
 * @param {string} companyCode
 */
export const checkCompanyCodePromise = async ($, lambda, companyCode) => {
    return genericLambdaPromise($, lambda, [companyCode]);
};

/**
 * @param {BrickContext | Value} $
 * @param {*} lambda
 * @param {string | null} code
 * @param {string | null} name
 * @param {string} companyCode
 * @param {string} purchaseOrg
 * @return {Promise<string>}
 */
export const getSuppliersByCodeOrNameLambda = async ($, lambda, code, name, companyCode, purchaseOrg) => {
    return genericLambdaPromise($, lambda, [code, name, companyCode, purchaseOrg]);
};

/**
 * @param {BrickContext | Value} $
 * @param {*} lambda
 * @param {string} queryValue
 * @param {string} queryType
 * @param {string} companyCode
 * @param {string} purchaseOrg
 * @return {Promise<string>}
 */
export const getSuppliersLambda = async ($, lambda, queryValue, queryType, companyCode, purchaseOrg) => {
    return genericLambdaPromise($, lambda, [queryValue, queryType, companyCode, purchaseOrg]);
};

/**
 * @param {BrickContext | Value} $
 * @param {*} lambda
 * @param {string} tag
 * @return {Promise<string>}
 */
export const openCandexLambda = async ($, lambda, tag) => {
    return genericLambdaPromise($, lambda, [tag]);
};

/**
 * @param {BrickContext} $
 * @param {*} lambda
 * @param {string} prJson
 * @return {Promise<string>}
 */
export const getTaxAmountLambda = ($, lambda, prJson) => {
    return genericLambdaPromise($, lambda, [prJson]);
};


/**
 * @param {BrickContext | Value} $
 * @param {*} lambda
 * @param {string} costCenter
 * @param {string} companyCode
 * @return {Promise<string>}
 */
export const getCostCenterLambda = async ($, lambda, costCenter, companyCode) => {
    return genericLambdaPromise($, lambda, [costCenter, companyCode]);
};


/**
 * @param {BrickContext | Value} $
 * @param {*} lambda
 * @param {string} vendorCode
 * @param {string} requesterId
 * @return {Promise<string>}
 */
export const getSupplierAddressLambda = async ($, lambda, vendorCode, requesterId) => {
    return genericLambdaPromise($, lambda, [vendorCode, requesterId]);
};


/**
 * @param {BrickContext | Value} $
 * @param {*} lambda
 * @param {string} userID
 * @param {string} wbs
 * @param {string} companyCode
 * @return {Promise<string>}
 */
export const getWBSLambda = async ($, lambda, userID, wbs, companyCode) => {
    return genericLambdaPromise($, lambda, [userID, wbs, companyCode]);
};

/**
 * @param {BrickContext | Value} $
 * @param {*} lambda
 * @param {string} materialNumber
 * @param {string} plant
 * @return {Promise<string>}
 */
export const getTaxCodeFromMaterialLambda = async ($, lambda, materialNumber, plant) => {
    return genericLambdaPromise($, lambda, [materialNumber, plant]);
};

/**
 * @param {BrickContext | Value} $
 * @param {*} lambda
 * @param {string} password
 * @return {Promise<string>}
 */
export const encryptPasswordLambda = async ($, lambda, password) => {
    return genericLambdaPromise($, lambda, [password]);
};

/**
 * @param {BrickContext | Value} $
 * @param {*} lambda
 * @param {string} vendorCode
 * @return {Promise<string>}
 */
export const getVendorNameLambda = async ($, lambda, vendorCode) => {
    return genericLambdaPromise($, lambda, [vendorCode]);
};


/**
 * @param {BrickContext | Value} $
 * @param {*} lambda
 * @param {string} password
 * @return {Promise<string>}
 */
export const decryptPasswordLambda = async ($, lambda, password) => {
    return genericLambdaPromise($, lambda, [password]);
};

/**
 * @param {BrickContext | Value} $
 * @param {*} lambda
 * @param {string?} companyCode
 * @return {Promise<string>}
 */
export const getPunchoutCatalogsInfoLambda = async ($, lambda, companyCode) => {
    return genericLambdaPromise($, lambda, [companyCode]);
};

/**
 * @param {BrickContext | Value} $
 * @param {*} lambda
 * @param {string} supplierCode
 * @param {string} supplierName
 * @param {string} supplierCompanyCode The company code of the supplier's catalog
 * @param {string} companyCode The company code of the PR
 * @param {string} purchaseOrg
 * @param {string} plant
 * @param {DeliveryAddress} deliveryAddress
 * @param {string} materialCode
 * @param {string} l3Category
 * @param {string} existingEPRId
 * @param {string} source
 * @return {Promise<string>}
 */
export const getPunchoutCatalogURLLambda = async ($, lambda, supplierCode, supplierName, supplierCompanyCode, companyCode, purchaseOrg, plant, deliveryAddress, materialCode, l3Category, existingEPRId, source) => {
    return genericLambdaPromise($, lambda, [supplierCode, supplierName, supplierCompanyCode, companyCode, purchaseOrg, plant, deliveryAddress, materialCode, l3Category, existingEPRId, source]);
};

/**
 * @param {BrickContext | Value} $
 * @param {*} lambda
 * @param {string} emails
 * @return {Promise<string>}
 */
export const getUserDetailsFromEmailLambda = async ($, lambda, emails) => {
    return genericLambdaPromise($, lambda, [emails]);
};

/**
 * @param {BrickContext} $
 * @param {any} lambda
 * @param {Map} headers
 * @param {Map} params
 * @param {Map} query
 * @param {string} body
 * @return {Promise<string>}
 */
export const preHandlerLambda = async ($, lambda, headers, params, query, body) => {
    return genericLambdaPromise($, lambda, [headers, params, query, body]);
};

/**
 * @param {BrickContext} $
 * @param {any} lambda
 * @param {Map} headers
 * @param {Map} params
 * @param {Map} query
 * @param {string} body
 * @param {TimeoutMap} cacheMemory
 * @return {Promise<string>}
 */
export const handlerLambda = async ($, lambda, headers, params, query, body, cacheMemory) => {
    return genericLambdaPromise($, lambda, [headers, params, query, body, cacheMemory]);
};

/**
 * @param {BrickContext} $
 * @param {any} lambda
 * @param {Map} headers
 * @param {Map} params
 * @param {Map} query
 * @param {string} body
 * @return {Promise<string>}
 */
export const handlerWithoutCacheLambda = async ($, lambda, headers, params, query, body) => {
    return genericLambdaPromise($, lambda, [headers, params, query, body]);
};

/**
 * @param {BrickContext} $
 * @param {any} lambda
 * @param {string} property
 * @param {string[]} valuesToSearch
 * @param {string} userId
 * @param {string} systemAlias
 * @return {Promise<string>}
 */
export const getMultipleMasterDataAPIV3Lambda = async ($, lambda, property, valuesToSearch, userId, systemAlias) => {
    return genericLambdaPromise($, lambda, [property, valuesToSearch, userId, systemAlias]);
};

/**
 * @param {BrickContext} $
 * @param {any} lambda
 * @param {string} username
 * @return {Promise<string>}
 */
export const prCountLambda = async ($, lambda, username) => {
    return genericLambdaPromise($, lambda, [username]);
};

/**
 * @param {BrickContext} $
 * @param {any} lambda
 * @param {string} currentTab
 * @param {boolean} myRequestOnly
 * @param {string} userId
 * @param {string} field
 * @param {string} searchInput
 * @return {Promise<string>}
 */
export const getFiltersDropdownsOptionsLambda = async ($, lambda, currentTab, myRequestOnly, userId, field, searchInput) => {
    return genericLambdaPromise($, lambda, [currentTab, myRequestOnly, userId, field, searchInput]);
};


/**
 * @param {BrickContext} $
 * @param {any} lambda
 * @param {GridFilterModel} model
 * @return {Promise<string>}
 */
export const onFilterChangeLambda = async ($, lambda, model) => {
    return genericLambdaPromise($, lambda, [model]);
};

/**
 * @param {BrickContext} $
 * @param {any} lambda
 * @param {*} item
 * @param {number} rank
 * @param {!ListDef|![]|!QueryResult} list
 * @return {Promise<string>}
 */
export const forEachWithErrorFlowLambda = async ($, lambda, item, rank, list) => {
    return genericLambdaPromise($, lambda, [item, rank, list]);
};

/**
 * @param {BrickContext} $
 * @param {any} lambda
 * @param {string} userEmail
 * @param {string} userId
 * @return {Promise<string>}
 */
export const findUnassignedPRsLambda = async ($, lambda, userEmail, userId) => {
    return genericLambdaPromise($, lambda, [userEmail, userId]);
};

/**
 * @param {BrickContext} $
 * @param {any} lambda
 * @param {string[]} userIDs
 * @return {Promise<string>}
 */
export const readUsersByUserIDsLambda = async ($, lambda, userIDs) => {
    return genericLambdaPromise($, lambda, [userIDs]);
};

/**
 * @param {BrickContext} $
 * @param {any} lambda
 * @param {string} searchString
 * @return {Promise<string>}
 */
export const readUsersLambda = async ($, lambda, searchString) => {
    return genericLambdaPromise($, lambda, [searchString]);
};

/**
 * @param {BrickContext} $
 * @param {any} lambda
 * @param {string} tag
 * @return {Promise<string>}
 */
export const openNestBuyCatalogLambda = async ($, lambda, tag) => {
    return genericLambdaPromise($, lambda, [tag]);
};
